import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { FileUpload } from "./FileUpload";
import { Combobox } from "./Combobox";
import { Eye, EyeOff } from "lucide-react";

function PasswordInput({ id, value, placeholder, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? "text" : "password"} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="pr-10" data-testid={id} autoComplete="new-password" />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Lihat password" data-testid={`${id}-toggle`}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
    </div>
  );
}
import { NameList } from "./NameList";
import { InvoiceList } from "./InvoiceList";
import { CatalogItems } from "./CatalogItems";
import { MoneyInput } from "./MoneyInput";

export function FormField({ f, value, onChange }) {
  const id = `field-${f.name}`;
  if (f.type === "money") {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} className="data-label">{f.label}{f.required && " *"}</Label>
        <MoneyInput id={id} value={value} onChange={onChange} placeholder={f.placeholder} testId={id} readOnly={f.readOnly} />
      </div>
    );
  }
  if (f.type === "password") {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} className="data-label">{f.label}{f.required && " *"}</Label>
        <PasswordInput id={id} value={value ?? ""} placeholder={f.placeholder} onChange={onChange} />
      </div>
    );
  }
  if (f.type === "catalogitems") {
    return (
      <div className="space-y-1.5">
        <Label className="data-label">{f.label}{f.required && " *"}</Label>
        <CatalogItems value={Array.isArray(value) ? value : []} onChange={onChange} kinds={f.kinds} single={f.single} testId={`field-${f.name.replace(/\./g, "-")}`} />
      </div>
    );
  }
  if (f.type === "invoicelist") {
    return (
      <div className="space-y-1.5">
        <Label className="data-label">{f.label}{f.required && " *"}</Label>
        <InvoiceList value={Array.isArray(value) ? value : []} onChange={onChange} testId={`field-${f.name.replace(/\./g, "-")}`} />
      </div>
    );
  }
  if (f.type === "namelist") {
    return (
      <div className="space-y-1.5">
        <Label className="data-label">{f.label}{f.required && " *"}</Label>
        <NameList value={Array.isArray(value) ? value : (value ? String(value).split("\n") : [])} onChange={onChange} placeholder={f.placeholder || "Nama"} testId={`field-${f.name.replace(/\./g, "-")}`} />
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} className="data-label">{f.label}{f.required && " *"}</Label>
        <Select value={value ?? ""} onValueChange={onChange}>
          <SelectTrigger id={id} data-testid={id}><SelectValue placeholder={f.placeholder || "Pilih..."} /></SelectTrigger>
          <SelectContent>{(f.options || []).map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${id}-opt-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (f.type === "combobox") {
    return (
      <div className="space-y-1.5">
        <Label className="data-label">{f.label}{f.required && " *"}</Label>
        <Combobox value={value ?? ""} onChange={onChange} options={f.options || []} placeholder={f.placeholder || "Ketik untuk mencari..."} testId={id} />
      </div>
    );
  }
  if (f.type === "switch") {
    return (
      <div className="flex items-center justify-between rounded-md border p-3">
        <Label htmlFor={id} className="text-sm">{f.label}</Label>
        <Switch id={id} checked={!!value} onCheckedChange={onChange} data-testid={id} />
      </div>
    );
  }
  if (f.type === "textarea") {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id} className="data-label">{f.label}{f.required && " *"}</Label>
        <Textarea id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} rows={3} data-testid={id} />
      </div>
    );
  }
  if (f.type === "file") {
    return (
      <div className="space-y-1.5">
        <Label className="data-label">{f.label}</Label>
        <FileUpload value={value || []} onChange={onChange} testId={id} />
      </div>
    );
  }
  if (f.type === "multiselect") {
    const arr = value || [];
    return (
      <div className="space-y-1.5">
        <Label className="data-label">{f.label}</Label>
        <div className="flex flex-wrap gap-2">
          {(f.options || []).map((o) => {
            const on = arr.includes(o.value);
            return (
              <button type="button" key={o.value} data-testid={`${id}-${o.value}`} onClick={() => onChange(on ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}>{o.label}</button>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="data-label">{f.label}{f.required && " *"}</Label>
      <Input id={id} type={f.type || "text"} value={value ?? ""} placeholder={f.placeholder} data-testid={id} readOnly={f.readOnly} className={f.readOnly ? "bg-muted/50" : undefined}
        onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
    </div>
  );
}

export function CrudDialog({ open, onOpenChange, title, fields, initial, basePath, onSaved, method }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(initial ? { ...initial } : Object.fromEntries(fields.map((f) => [f.name, f.default ?? (f.type === "switch" ? false : f.type === "multiselect" || f.type === "file" ? [] : "")])));
  }, [open, initial, fields]);

  const submit = async (e) => {
    e.preventDefault();
    const visible = fields.filter((f) => !f.showIf || f.showIf(form));
    for (const f of visible) if (f.required && (form[f.name] === "" || form[f.name] == null)) return toast.error(`${f.label} wajib diisi`);
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([k]) => fields.some((f) => f.name === k) || k === "id"));
      const res = initial?.id ? await api.put(`${basePath}/${initial.id}`, payload) : await api[method || "post"](basePath, payload);
      toast.success(initial?.id ? "Data diperbarui" : "Data ditambahkan");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (er) { toast.error(errMsg(er)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="crud-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit" : "Tambah"} {title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.filter((f) => !f.showIf || f.showIf(form)).map((f) => (
              <div key={f.name} className={f.full || f.type === "textarea" || f.type === "file" || f.type === "multiselect" || f.type === "combobox" ? "sm:col-span-2" : ""}>
                <FormField f={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="crud-cancel-btn">Batal</Button>
            <Button type="submit" disabled={saving} data-testid="crud-submit-btn">{saving ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
